import { memo, useCallback } from 'react';

import { type CardSize } from '@air/api/types';
import { SegmentedControl, type SegmentedControlOption } from '@air/component-segmented-control';
import { ChevronDownIcon } from '@air/design-icons';
import { input } from '@air/primitive-input';

import { useCardSizePreference } from '~/hooks/useCardSizePreference';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

const CARD_SIZE_OPTIONS: Record<CardSize, SegmentedControlOption> = {
  small: {
    id: 'small',
    label: 'Small',
    testId: 'VIEW_CARD_SIZE_OPTION_SMALL',
    tooltip: 'Small',
    value: 'small',
  },
  medium: {
    id: 'medium',
    label: 'Medium',
    testId: 'VIEW_CARD_SIZE_OPTION_MEDIUM',
    tooltip: 'Medium',
    value: 'medium',
  },
  large: {
    id: 'large',
    label: 'Large',
    testId: 'VIEW_CARD_SIZE_OPTION_LARGE',
    tooltip: 'Large',
    value: 'large',
  },
  'extra-large': {
    id: 'extra-large',
    label: 'Extra Large',
    testId: 'VIEW_CARD_SIZE_OPTION_EXTRA_LARGE',
    tooltip: 'Extra Large',
    value: 'extra-large',
  },
};

// Both the segmented control and the legacy select render in this order.
const CARD_SIZE_ORDER: CardSize[] = ['small', 'medium', 'large', 'extra-large'];

const CARD_SIZE_OPTION_LIST = CARD_SIZE_ORDER.map((size) => CARD_SIZE_OPTIONS[size]);

const isCardSize = (value: string): value is CardSize => value in CARD_SIZE_OPTIONS;

export const ViewCardSizePreference = memo(() => {
  // Read here rather than at the call sites: this control is shared by the private and public menus, which both
  // have to keep the legacy select while the redesign is gated.
  const { ui2026 } = useTemporaryFeatureFlags();
  const { cardSize, updateCardSize } = useCardSizePreference();
  // Stored sizes are unvalidated localStorage/saved-view values, and the segmented control throws on an unknown
  // option where the legacy select tolerated it.
  const selectedCardSizeOption = isCardSize(cardSize) ? CARD_SIZE_OPTIONS[cardSize] : CARD_SIZE_OPTIONS.medium;

  const handleSelect = useCallback(
    (option: SegmentedControlOption) => {
      // Options only ever come from CARD_SIZE_OPTIONS, so the guard narrows rather than filters.
      if (isCardSize(option.id)) {
        updateCardSize(option.id);
      }
    },
    [updateCardSize],
  );

  if (ui2026) {
    return (
      <div className="flex flex-col gap-1.5 px-1.5" data-testid="VIEW_CARD_SIZE">
        <span className="font-medium ads:text-primary text-14 text-gray-12">Size</span>
        {/* Not fullWidth: four segments sharing the popover would leave "Extra Large" narrower than its label. */}
        <SegmentedControl
          appearance="rectangle"
          color="gray"
          onSelect={handleSelect}
          options={CARD_SIZE_OPTION_LIST}
          outerAppearance="default"
          selectedOption={selectedCardSizeOption}
          size="xsmall"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-6 px-1.5 py-1">
      <label className="text-14" htmlFor="preferences">
        Size
      </label>
      <div className={input({ className: 'relative px-0 text-12', size: 'small' })}>
        <select
          id="cardSize"
          data-testid="VIEW_CARD_SIZE_SELECT"
          defaultValue={cardSize}
          className="size-full cursor-pointer appearance-none bg-transparent pr-8 pl-2"
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
          onChange={(event) => {
            // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
            updateCardSize(event.target.value as CardSize);
          }}
          value={cardSize}
        >
          {CARD_SIZE_ORDER.map((size) => (
            <option key={size} value={size}>
              {CARD_SIZE_OPTIONS[size].label}
            </option>
          ))}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2" size="sm" />
      </div>
    </div>
  );
});

ViewCardSizePreference.displayName = 'ViewCardSizePreference';
